import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {
  "pageTitle": "Form Group API",
  "title": "Form Group",
  "props": ["FormGroupProps"]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const LeadParagraph = makeShortcode("LeadParagraph");
const Link = makeShortcode("Link");
const FormGroupProps = makeShortcode("FormGroupProps");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <PageContent componentName="form_group" type="api" mdxType="PageContent">
      <LeadParagraph mdxType="LeadParagraph">
  FormGroup components wrap controls such as the{' '}
  <Link to="/api/checkboxes" mdxType="Link">Checkbox</Link> and{' '}
  <Link to="/api/toggle" mdxType="Link">Toggle</Link> components. For{' '}
  <Link to="/api/radio" mdxType="Link">Radio Buttons</Link>, you should use the{' '}
  <Link to="/api/radio/#radio_group_props" mdxType="Link">Radio Group</Link> component.
      </LeadParagraph>
      <h2>{`Basic Usage`}</h2>
      <p>{`Form groups are not necessary when displaying only a single checkbox or toggle. However, if more than one checkbox or toggle fit together to answer a single question,
form groups should be used to make that association, both visually and for users of assistive technology.`}</p>
      <p>{`The label/group association will be handled via the passed in `}<inlineCode parentName="p">{`id`}</inlineCode>{` if one is supplied or via an auto-generated `}<inlineCode parentName="p">{`id`}</inlineCode>{` if not supplied.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { FormGroup, Checkbox, Toggle } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <FormGroup labelText="Choose one or more colors">
        <Checkbox labelText="Red" />
        <Checkbox labelText="Blue" />
        <Checkbox labelText="Purple" />
      </FormGroup>
      <br />
      <FormGroup
        id="myCustomId"
        labelText="Turn on notifications for the following"
      >
        <Toggle labelText="Product updates" />
        <Toggle labelText="Promotions and sales" />
      </FormGroup>
    </>
  );
}
`}</code></pre>
      <h2>{`Labeling Form Groups`}</h2>
      <p>{`To meet accessibility requirements, a form group `}<em parentName="p">{`must`}</em>{` have a label. You can provide a label in one of two ways.`}</p>
      <p>{`You can use the `}<inlineCode parentName="p">{`labelText`}</inlineCode>{` prop, which takes a string or a React Node. This will be displayed on screen inside a `}<inlineCode parentName="p">{`<label>`}</inlineCode>{` element and be associated with your form group.`}</p>
      <p>{`Or you can use the `}<inlineCode parentName="p">{`labelledById`}</inlineCode>{` prop, which also takes a string. This string should refer to an ID of an element that exists somewhere else on the page.
This id will become the value of the `}<inlineCode parentName="p">{`aria-labelledby`}</inlineCode>{` attribute for the group.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { FormGroup, Checkbox, Heading } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <FormGroup
        labelText={
          <>
            Labelled by <i>label</i>
          </>
        }
      >
        <Checkbox labelText="Label" />
        <Checkbox labelText="Label" />
      </FormGroup>
      <FormGroup labelledById="h3Label">
        <Heading id="h3Label" level={3}>
          Labelled by H3
        </Heading>
        <Checkbox labelText="Label" />
        <Checkbox labelText="Label" />
      </FormGroup>
    </>
  );
}
`}</code></pre>
      <h2>{`Custom Styles`}</h2>
      <p>{`Custom styles can be applied by using the `}<inlineCode parentName="p">{`containerStyle`}</inlineCode>{` and `}<inlineCode parentName="p">{`labelStyle`}</inlineCode>{` props.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { FormGroup, Checkbox } from 'react-magma-dom';

export function Example() {
  return (
    <FormGroup
      containerStyle={{ border: '1px solid gray', padding: '10px' }}
      labelStyle={{ fontWeight: '500', color: 'red' }}
      labelText="Group label"
    >
      <Checkbox labelText="Checkbox label" />
      <Checkbox labelText="Checkbox label" />
    </FormGroup>
  );
}
`}</code></pre>
      <h2>{`Visually Hidden Label`}</h2>
      <p>{`The label text for the form group can be hidden visually, but still be read by assistive technology, by using the `}<inlineCode parentName="p">{`isTextVisuallyHidden`}</inlineCode>{` prop.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { FormGroup, Checkbox } from 'react-magma-dom';

export function Example() {
  return (
    <FormGroup labelText="Group Label" isTextVisuallyHidden>
      <Checkbox labelText="Checkbox label" />
      <Checkbox labelText="Checkbox label" />
    </FormGroup>
  );
}
`}</code></pre>
      <h2>{`Inverse`}</h2>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { FormGroup, Checkbox, Card, CardBody } from 'react-magma-dom';

export function Example() {
  return (
    <Card isInverse>
      <CardBody>
        <FormGroup labelText="Choose one or more colors" isInverse>
          <Checkbox labelText="Red" isInverse />
          <Checkbox labelText="Blue" isInverse />
          <Checkbox labelText="Purple" isInverse />
        </FormGroup>
      </CardBody>
    </Card>
  );
}
`}</code></pre>
      <h2>{`Form Group Props`}</h2>
      <p><strong parentName="p">{`This component uses `}<inlineCode parentName="strong">{`forwardRef`}</inlineCode>{`. The ref is applied to the Form Group `}<inlineCode parentName="strong">{`div`}</inlineCode>{` element.`}</strong></p>
      <p>{`All of the `}<a parentName="p" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes"
        }}>{`global HTML attributes`}</a>{` can be provided as props and will be applied to the wrapping `}<inlineCode parentName="p">{`div`}</inlineCode>{` element that gets rendered.`}</p>
      <FormGroupProps mdxType="FormGroupProps" />
    </PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      